import { useContext, useState } from "react";
import {
  Box,
  Divider,
  Flex,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Text,
  Tooltip,
  useDisclosure,
} from "@chakra-ui/react";
import { Bell, Building2, History, KeyRound, ListTodo, LogOut, Settings, UserCog } from "lucide-react";
import { LogoutModal } from "../../pages/Users/modais/LogoutModal";
import { checkCollaboratorPermission } from "../../pages/Users/utils/checkPermission";
import { NotificationContext, useAppData, useConfigGuard } from "../../contexts/AppContext";
import { ClientSwitchModal } from "./ClientSwitchModal";
import { topNavLayers } from "./topNavLayers";

/**
 * Menu de conta da barra global (GEN-3497).
 *
 * Aqui mora tudo que é "meu": perfil, senha, configurações, cliente ativo e sair.
 *
 * r3 — duas mudanças pedidas pelo Thiago:
 *
 * 1. **Um lugar só para trocar a senha.** Antes havia dois: este menu abria o `UserModal`
 *    (que tem aba de senha) e Configurações tem a tela "Trocar senha". Agora o menu apenas
 *    NAVEGA para Configurações; quem edita perfil e senha é sempre a mesma tela.
 * 2. **A troca de cliente veio para cá**, saindo da trilha — é o racional do Notion (a
 *    trilha diz onde estou no produto; a conta diz em nome de quem).
 */
export const AccountMenu = () => {
  const { validation, currentClient } = useAppData();
  const { requestNavigation } = useConfigGuard();
  const unread = useContext(NotificationContext).unread ?? 0;

  const logout = useDisclosure();
  const clientSwitch = useDisclosure();
  const [menuOpen, setMenuOpen] = useState(false);

  const canOpenConfig = checkCollaboratorPermission(validation, currentClient?.guid_client as string, "config.read");

  // "Minhas tarefas" só existe para quem tem o Kanban no cliente e permissão de leitura nele — o
  // mesmo par que a sidebar exigia quando o item morava lá. Sem isso o item viraria atalho para um
  // redirect silencioso.
  const canSeeTasks = Boolean(
    currentClient?.products?.kanban &&
      checkCollaboratorPermission(validation, currentClient?.guid_client as string, "kanban.read")
  );
  const hasManyClients = (validation?.clients?.length ?? 0) > 1;

  // SEMPRE duas letras: primeira do primeiro nome + primeira do último. Quando a pessoa está
  // cadastrada só com o primeiro nome ("Thiago"), duas iniciais não existem — aí valem as duas
  // primeiras letras dele, para o avatar não ficar com uma letra solta e perder a distinção entre
  // duas pessoas do mesmo time (pedido do Thiago em 25/08, olhando o avatar em HMLG).
  const partes = (validation?.name ?? "").trim().split(/\s+/).filter(Boolean);
  const initials = (
    partes.length > 1 ? partes[0][0] + partes[partes.length - 1][0] : (partes[0] ?? "").slice(0, 2)
  )
    .toUpperCase()
    .slice(0, 2);

  const go = (path: string) => {
    setMenuOpen(false);
    requestNavigation(path);
  };

  return (
    <>
      <Menu
        isOpen={menuOpen}
        onOpen={() => setMenuOpen(true)}
        onClose={() => setMenuOpen(false)}
        placement="bottom-end"
        isLazy
      >
        <Tooltip label="Minha conta" openDelay={600}>
          <MenuButton
            aria-label={`Minha conta — ${validation?.name ?? ""}`}
            borderRadius="50%"
            sx={{
              transition: "box-shadow 150ms ease",
              "&:hover": { boxShadow: "0 0 0 3px #E2E8F0" },
              "&:focus-visible": { boxShadow: "0 0 0 2px #375CA9", outline: "none" },
            }}
          >
            {/* Avatar próprio: o <Avatar> do Chakra pinta o fundo por variável CSS interna
                e ignora a prop `bg` — as iniciais saíam brancas sobre transparente. */}
            <Flex
              align="center"
              justify="center"
              w="32px"
              h="32px"
              borderRadius="50%"
              // Tom leve: o avatar é IDENTIDADE, não ação. Em azul sólido ele competia com os
              // botões primários da tela e puxava o olho para o canto o tempo todo (Thiago, 25/08).
              backgroundColor="#EEF0FF"
              color="#4B4EFF"
              fontSize="12px"
              fontWeight="600"
              lineHeight="1"
            >
              {initials}
            </Flex>
          </MenuButton>
        </Tooltip>

        <MenuList zIndex={topNavLayers.panel} minW="260px" py="8px" boxShadow="lg">
          <Box px="14px" pb="8px">
            <Text fontSize="14px" fontWeight="600" color="#303033" noOfLines={1}>
              {validation?.name}
            </Text>
            <Text fontSize="12px" color="#77777B" noOfLines={1}>
              {validation?.email}
            </Text>
          </Box>
          <Divider mb="4px" />

          {/* O cliente ativo aparece SEMPRE — é contexto que saiu da trilha e não pode
              simplesmente sumir. Vira ação só quando há mais de um. */}
          {hasManyClients ? (
            <MenuItem
              fontSize="14px"
              icon={<Building2 size={16} />}
              onClick={() => {
                setMenuOpen(false);
                clientSwitch.onOpen();
              }}
            >
              <Flex direction="column" gap="1px" minW="0">
                <Text fontSize="14px" noOfLines={1}>
                  Trocar de cliente
                </Text>
                <Text fontSize="12px" color="#77777B" noOfLines={1}>
                  {currentClient?.name}
                </Text>
              </Flex>
            </MenuItem>
          ) : (
            <Flex px="14px" py="6px" gap="10px" align="center" color="#77777B">
              <Building2 size={16} />
              <Text fontSize="13px" noOfLines={1} title={currentClient?.name ?? ""}>
                {currentClient?.name}
              </Text>
            </Flex>
          )}

          <Divider my="4px" />

          {/* GEN-3497 (r9): "Meu histórico" e "Minhas tarefas" saíram da sidebar e vieram para cá.
              Os dois são do USUÁRIO, não do cliente — é o mesmo critério que trouxe conta, senha e
              notificações para o menu do avatar. A sidebar fica só com navegação entre produtos. */}
          <MenuItem fontSize="14px" icon={<History size={16} />} onClick={() => go("/historico")}>
            Meu histórico
          </MenuItem>
          {/* GEN-4100 — a central de notificações também é do USUÁRIO; antes só se chegava a ela pelo
              rodapé do sino. O contador é o mesmo do sino (não lidas dos últimos 30 dias). */}
          <MenuItem fontSize="14px" icon={<Bell size={16} />} onClick={() => go("/notificacoes")}>
            <Flex align="center" justify="space-between" w="100%" gap="8px">
              <span>Notificações</span>
              {unread > 0 ? (
                <Box
                  as="span"
                  minW="20px"
                  h="18px"
                  px="6px"
                  borderRadius="9px"
                  bg="#FEE2E2"
                  color="#B42318"
                  borderWidth="1px"
                  borderColor="#FECDCA"
                  fontSize="11px"
                  fontWeight="500"
                  display="inline-flex"
                  alignItems="center"
                  justifyContent="center"
                  aria-label={`${unread} não lida${unread > 1 ? "s" : ""}`}
                >
                  {unread > 99 ? "99+" : unread}
                </Box>
              ) : null}
            </Flex>
          </MenuItem>
          {canSeeTasks ? (
            <MenuItem fontSize="14px" icon={<ListTodo size={16} />} onClick={() => go("/tarefas")}>
              Minhas tarefas
            </MenuItem>
          ) : null}

          <Divider my="4px" />

          {/* Perfil e senha são AUTOATENDIMENTO e ficam fora do gate: na sidebar antiga o
              modal de conta era montado sem permissão nenhuma, e amarrá-los a `config.read`
              — que é a permissão de administrar o CLIENTE — tirava de um colaborador
              operacional a única forma de trocar a própria senha. */}
          <MenuItem fontSize="14px" icon={<UserCog size={16} />} onClick={() => go("/configuracoes?secao=account")}>
            Meu perfil
          </MenuItem>
          <MenuItem
            fontSize="14px"
            icon={<KeyRound size={16} />}
            onClick={() => go("/configuracoes?secao=changePassword")}
          >
            Trocar senha
          </MenuItem>

          {/* "Configurações" continua sendo o item administrativo: quem não administra o
              cliente não veria nada além do que os dois itens acima já abrem. */}
          {canOpenConfig ? (
            <MenuItem fontSize="14px" icon={<Settings size={16} />} onClick={() => go("/configuracoes")}>
              Configurações
            </MenuItem>
          ) : null}

          <Divider my="4px" />

          <MenuItem
            fontSize="14px"
            color="#BA1B1B"
            icon={<LogOut size={16} />}
            onClick={() => {
              setMenuOpen(false);
              logout.onOpen();
            }}
          >
            Sair
          </MenuItem>
        </MenuList>
      </Menu>

      <ClientSwitchModal isOpen={clientSwitch.isOpen} onClose={clientSwitch.onClose} />
      <LogoutModal st="controlled" isOpen={logout.isOpen} onClose={logout.onClose} />
    </>
  );
};
